/**
 * Type definitions for the qduels-api `/v1/*` namespace.
 *
 * QDuels is the QLASH 1v1 duel-challenge microservice (Clash Royale today):
 * the challenger picks a friend, qduels creates a `pending` challenge, the
 * defender accepts/declines in-app, and on accept BOTH players are told to open
 * Clash Royale and play a friendly battle manually — the Supercell API is
 * read-only, so qduels does NOT (and cannot) create an in-game invite; instead
 * a polling worker detects the played match and tallies the result.
 *
 * Hand-copied from the qduels-api contract (no codegen) — keep in sync when
 * the server contract changes. Endpoint base: `https://qduels.qlash.gg`.
 */

/** Best-of length for a duel. Defaults to `bo3` in the UI. */
export type DuelFormat = 'bo1' | 'bo3' | 'bo5';

/**
 * Game modes exposed in the duel picker. qduels-api accepts `gameMode` as a
 * free string server-side (any Supercell CrGameMode); the Mini App only
 * surfaces this curated set. NB: `duel` MUST stay lowercase — Supercell expects
 * it that way (all the others are upper-case). `duel` is itself a best-of-3
 * within one match, so combined with a bo3/bo5 series it's heavy — kept on
 * purpose, just be aware.
 */
export type DuelGameMode =
  | 'REGULAR'
  | 'duel'
  | 'DOUBLE_ELIXIR'
  | 'TRIPLE_ELIXIR'
  | 'SUDDEN_DEATH'
  | 'RAMP_UP'
  | 'TOUCHDOWN'
  | 'HEIST';

/** Game modes shown in the challenge picker, in display order (REGULAR default). */
export const DUEL_GAME_MODES: readonly DuelGameMode[] = [
  'REGULAR',
  'duel',
  'DOUBLE_ELIXIR',
  'TRIPLE_ELIXIR',
  'SUDDEN_DEATH',
  'RAMP_UP',
  'TOUCHDOWN',
  'HEIST',
];

/** Best-of options shown in the challenge picker (bo3 default). */
export const DUEL_FORMATS: readonly DuelFormat[] = ['bo1', 'bo3', 'bo5'];

/** i18n key for a best-of label — e.g. 'bo3' → 'social.duel_format_bo3'. */
export function duelFormatLabelKey(format: string): string {
  return `social.duel_format_${format}`;
}

/** i18n key for a game-mode label — e.g. 'TOUCHDOWN' → 'social.duel_mode_TOUCHDOWN'. */
export function duelGameModeLabelKey(mode: string): string {
  return `social.duel_mode_${mode}`;
}

/** Surface that originated the challenge (telemetry on the qduels side). */
export type DuelCreatedVia = 'telegram';

/**
 * Lifecycle status of a challenge (mirrors qduels-api `Challenge.status`).
 * `pending` → defender accepts → `in_progress` (a hosted Supercell match is
 * created and both players get the in-game invite) → `completed`. Other
 * terminal states: `declined`, `cancelled`, `expired`, `abandoned`.
 */
export type ChallengeStatus =
  | 'pending'
  | 'accepted'
  | 'in_progress'
  | 'declined'
  | 'expired'
  | 'cancelled'
  | 'completed'
  | 'abandoned';

/**
 * Request body for `POST /v1/challenges`.
 *
 * `defenderLogtoId` is the friend's canonical QLASH user id (Logto `sub`) —
 * the same value QSocials stores as `friendUserId`. The challenger is taken
 * from the Bearer JWT `sub` server-side, so it is not sent here.
 */
export interface CreateChallengeRequest {
  defenderLogtoId: string;
  format: DuelFormat;
  gameMode: DuelGameMode;
  createdVia: DuelCreatedVia;
}

/** Participant projection inside a challenge object (qduels `challenger`/`defender`). */
export interface ChallengeParticipant {
  logtoUserId: string;
  playerTag: string;
  displayName?: string | null;
}

/**
 * A challenge object as returned by qduels-api (`POST /v1/challenges` 201,
 * `GET /v1/challenges` list items, and the accept/decline/cancel responses).
 * The server returns the full document unwrapped (no `{ data }` envelope).
 */
export interface ChallengeDTO {
  challengeId: string;
  status: ChallengeStatus;
  challenger: ChallengeParticipant;
  defender: ChallengeParticipant;
  format: DuelFormat;
  gameMode?: string;
  winsChallenger?: number;
  winsDefender?: number;
  currentMatchTag?: string | null;
  /**
   * CR game tags of the matches played in this challenge (bo3/bo5), in order.
   * Present in the qduels-api response; the last entry is the decisive/most
   * recent match — what the Sfide-tab "completed" tap opens in DuelResult.
   */
  matchTags?: string[];
  winnerLogtoUserId?: string | null;
  resultReason?: string | null;
  /** ISO timestamp after which a still-`pending` challenge auto-expires. */
  deadline?: string;
  createdVia?: string;
  createdAt?: string;
  updatedAt?: string;
}

/** Query for `GET /v1/challenges` (all optional). */
export interface ListChallengesQuery {
  role?: 'challenger' | 'defender';
  status?: ChallengeStatus;
  limit?: number;
}

/**
 * Error envelope for qduels 4xx responses: `{ "error": "<code>" }`.
 * Known codes the Mini App handles explicitly are enumerated in
 * `QduelsErrorCode`; any other string falls through to a generic message.
 */
export interface QduelsErrorBody {
  error: string;
}

/** Error codes qduels-api returns that the friends-list UI maps to copy. */
export type QduelsErrorCode =
  | 'defender_not_linked'
  | 'challenger_not_linked'
  | 'cannot_challenge_self'
  | 'missing_bearer_token'
  | 'invalid_token'
  // verify-token / link-status codes (POST /v1/players/verify-token, GET /v1/players/me)
  | 'player_tag_already_linked'
  | 'invalid_player_tag'
  | 'no_linked_player'
  // matchmaking-queue codes (POST /v1/queue/enter)
  | 'player_not_linked' // 400 — caller has no linked CR account
  | 'not_eligible' // 403 — CR account below the floor (≥32 cards, level ≥10)
  | 'already_in_match' // 409 — caller already has a live match
  // tournament codes (/v1/tournaments)
  | 'tournament_not_found' // 404 — unknown id (NB: also what a wrong id looks like)
  | 'tournament_full' // 409 — declaredSize reached
  | 'already_joined' // 409
  | 'not_an_organizer' // 403 — create: caller not in the organizer whitelist
  | 'not_the_organizer' // 403 — start/cancel: not your tournament
  | 'rate_limited' // 429 — Redis limiter (list 60/min, join 30/min)
  // Discovery-query contract (?scope=open). Returned as 400 `invalid_query`'s
  // richer cousins — qduels surfaces these codes verbatim.
  | 'scope_open_excludes_role'
  | 'scope_open_excludes_status'
  | 'cursor_requires_scope_open'
  | 'invalid_cursor'
  | 'invalid_query'
  // Supercell-origin failures on join — TRANSIENT infra, not "tournament gone".
  | 'cr_player_not_found' // 404 (misleading: reads like the tournament is missing)
  | 'cr_rate_limited' // 503 + Retry-After
  | 'cr_upstream_error' // 502
  // check-in codes (POST /v1/tournaments/:id/checkin, POST /v1/queue/checkin)
  | 'not_in_tournament' // 404 — you have no slot here
  | 'not_in_queue' // 404 — no pending pair
  | 'checkin_not_open' // 409 — not the moment (ALSO what a closed gate returns)
  | 'checkin_expired' // 410 — you missed the window (distinct from the above!)
  | 'queue_cooldown'; // 429 + retryAfter — no-show penalty

/**
 * `leave`/`start`/`cancel` refuse with a status-templated code —
 * `cannot_leave_in_progress`, `cannot_start_complete`, … (plus a literal
 * `cannot_cancel_unknown` when the doc vanished mid-request). Match by PREFIX,
 * never by equality, or a status we haven't seen slips through.
 */
export const QDUELS_CANNOT_PREFIXES = ['cannot_leave_', 'cannot_start_', 'cannot_cancel_'] as const;

// ─── Tournaments (mini-tournaments module, /v1/tournaments) ───────────────

/** Lifecycle: a strict one-way lattice. */
export type QduelsTournamentStatus = 'registration' | 'in_progress' | 'complete' | 'cancelled';

/**
 * `standard` — declaredSize is a MAX; starts manually or at `startAt`; byes fill
 * the gap. `sitngo` — declaredSize is EXACT (4/8/16/32 only), no `startAt`,
 * auto-starts the instant it fills (so byes are impossible), may be `recurring`.
 */
export type QduelsTournamentKind = 'standard' | 'sitngo';

/**
 * Discovery contract (qduels 2026-07-16). `public` is listed by
 * `GET /v1/tournaments?scope=open`; `community` is not — it is reachable only by
 * its organizer, its participants, or anyone holding its id.
 *
 * Deliberately NOT derived from `guildId == null` server-side: that would
 * conflate "created in a DM" with "meant for everyone".
 */
export type QduelsTournamentVisibility = 'public' | 'community';

/** A player slot reference. `playerName` is optional — may be absent/empty. */
export interface QduelsParty {
  logtoUserId: string;
  playerTag: string;
  playerName?: string;
}

/**
 * `GET /v1/tournaments` rows and `GET /v1/tournaments/:id`.
 *
 * ⚠️ Every date is an **ISO string** here, not a Date: qduels types these as
 * `Date` server-side but they cross the wire as JSON.
 */
export interface QduelsTournamentSummary {
  tournamentId: string;
  name: string;
  organizer: { logtoUserId: string; displayName: string };
  /** Only `single_elim` is implemented (the union declares more as future). */
  format: string;
  kind: QduelsTournamentKind;
  /** `standard` only, and null unless scheduled. Forbidden for `sitngo`. */
  startAt: string | null;
  recurring: boolean;
  /** 1 | 3 | 5 | 7 — a slot resolves at floor(N/2)+1 wins. */
  bestOf: number;
  declaredSize: number;
  /** null until start() builds the bracket. */
  bracketSize: number | null;
  gameMode: string;
  status: QduelsTournamentStatus;
  /** Server context (Discord guild id | `tg:<chatId>`); null = server-less. */
  guildId: string | null;
  visibility: QduelsTournamentVisibility;
  participantCount: number;
  currentRound: number;
  roundDeadlineMinutes: number | null;
  currentRoundOpenedAt: string | null;
  currentRoundDeadlineAt: string | null;
  /**
   * Prize in QLASH Coin, minted to the champion at completion.
   *
   * Prizes ARE live in PROD (read from the host 2026-07-16:
   * `TOURNAMENT_PRIZE_DEFAULT_QC=50`, `FEATURE_QBANK_EMIT=true`) — an earlier
   * note here claimed they were held at 0. They are not. Safe to render.
   *
   * ⚠️ But the prize is MINTED, not funded: no entry fee, no escrow, no organiser
   * debit. So don't word it as a stake ("montepremi messo in palio") — nobody put
   * it up. See docs/QDUELS-TMA-INTEGRATION.md §6.2.
   */
  prizeQc: number;
  /** null while unfinished, AND on a real draw of a completed tournament. */
  winner: QduelsParty | null;
  resultReason: string | null;
  registrationDeadline: string | null;
  startedAt: string | null;
  completedAt: string | null;
  createdVia: string;
  createdAt?: string;
}

/** `GET /v1/tournaments/:id` — summary plus a slot rollup. */
export interface QduelsTournamentDetail extends QduelsTournamentSummary {
  slots: {
    total: number;
    byStatus: Record<string, number>;
    byRound: { round: number; total: number; done: number }[];
  };
}

/**
 * `waiting` → `ready` → [`awaiting_checkin`] → `in_flight` → `done`.
 * `ready` ⇄ `in_flight` oscillates between the games of a bo-N series.
 *
 * `awaiting_checkin` only ever appears when qduels' `FEATURE_MATCH_CHECKIN` gate
 * is on: the pair is matched and the in-game invite is held until BOTH players
 * confirm. With the gate off the state never occurs — so the TMA needs no flag
 * of its own here, it just reacts to what the server reports.
 */
export type QduelsSlotStatus = 'waiting' | 'ready' | 'awaiting_checkin' | 'in_flight' | 'done';

export interface QduelsSlotPointer {
  slot: number;
  side: 'a' | 'b';
}

/** A bracket slot from `GET /v1/tournaments/:id/slots`. */
export interface QduelsTournamentSlot {
  slot: number;
  round: number;
  bracket: string;
  a: QduelsParty | null;
  b: QduelsParty | null;
  seedA: number | null;
  seedB: number | null;
  status: QduelsSlotStatus;
  winsA: number;
  winsB: number;
  winner: QduelsParty | null;
  /** e.g. `bye`, `walkover`, `forfeit` — drives the "why" copy. */
  resultReason: string | null;
  forfeitedBy: string | null;
  /**
   * ⚠️ null for most of a best-of series and BETWEEN games — null does NOT mean
   * "nothing to do". With status `ready` it means "next game starting…".
   */
  currentMatchTag: string | null;
  nextWin: QduelsSlotPointer | null;

  // ── Spectator deep links (2026-07-17) ──────────────────────────────────
  // Per-side `clashroyale://specHostedMatch?match_tag=&player_tag=<POV>` links,
  // present ONLY while this slot's match is actually being played (`inProgress`
  // server-side, surfaced here as `status: 'in_flight'` with a
  // `currentMatchTag`). null when pending / between games / terminal / bye —
  // so a non-null value IS the "watch now" signal; never render a watch button
  // on a null. `spectatorAurl` is player a's POV, `spectatorBurl` is b's.
  // Open to any authenticated caller (the bracket is public context), which is
  // what lets a third party watch a match they aren't in.
  spectatorAurl: string | null;
  spectatorBurl: string | null;

  // ── The check-in gate ──────────────────────────────────────────────────
  // Flat and per-side, deliberately NOT shaped like the queue's `checkin`
  // block: that one is caller-scoped (`checkedIn` = did *I*), while this
  // projection has no caller — it describes the BRACKET, not you. Same name +
  // different meaning across the two surfaces would be a trap. Work out your
  // own side by matching your playerTag against `a`/`b`.

  /** null unless the gate is open. Drives the countdown. */
  checkinDeadlineAt: string | null;
  /** Has side A confirmed? Reads `false` when the gate is off (nothing writes it). */
  checkedInA: boolean;
  checkedInB: boolean;
}

/** `POST /v1/tournaments/:id/checkin` — the server derives your slot. */
export interface QduelsTournamentCheckinResult {
  slot: number;
  checkedIn: true;
  bothCheckedIn: boolean;
}

/** `POST /v1/queue/checkin` — the server derives your pair. */
export interface QduelsQueueCheckinResult {
  pairId: string;
  checkedIn: true;
  bothCheckedIn: boolean;
}

export interface QduelsTournamentParticipant {
  logtoUserId: string;
  playerTag: string;
  displayName: string;
  trophiesAtJoin: number;
  seed?: number | null;
  joinedAt?: string;
}

/**
 * `GET /v1/tournaments?scope=open` — the ONLY list a stranger may see:
 * `visibility: 'public'`, caller-independent, keyset-paged.
 *
 * `status` selects WHICH public list:
 *   - omitted / `'registration'` → joinable, registration-open tournaments.
 *   - `'in_progress'` → the LIVE list: public tournaments being played right
 *     now. This is the only way a non-participant finds a match to watch.
 * `'complete'`/`'cancelled'` are NOT browsable here (400). And `role` is still
 * REJECTED with this scope (400) — pass it only on the caller-scoped list.
 * There is **no `kind` filter**: split standard vs sit&go client-side.
 */
export interface ListOpenTournamentsQuery {
  status?: 'registration' | 'in_progress';
  limit?: number;
  /** Opaque base64url keyset cursor from a previous page's `nextCursor`. */
  cursor?: string;
}

/** `GET /v1/tournaments` (default scope) — the caller's own tournaments. */
export interface ListMyTournamentsQuery {
  role?: 'organizer' | 'participant';
  status?: QduelsTournamentStatus;
  limit?: number;
}

/** `?scope=open` response — paged. */
export interface QduelsOpenTournamentsPage {
  tournaments: QduelsTournamentSummary[];
  nextCursor: string | null;
}

/**
 * Default-scope response. Deliberately has **no** `nextCursor`: qduels keeps
 * this shape byte-identical because QBot already consumes it.
 */
export interface QduelsMyTournamentsResponse {
  tournaments: QduelsTournamentSummary[];
}

/** `POST /:id/join` — an ad-hoc object, NOT a TournamentSummary. */
export interface QduelsTournamentJoinResult {
  tournamentId: string;
  playerTag: string;
  trophiesAtJoin: number;
  participantCount: number;
}

/** `POST /:id/leave` — permitted only during `registration`. */
export interface QduelsTournamentLeaveResult {
  tournamentId: string;
  participantCount: number;
}

/** `GET /:id/slots` — `nextCursor` (id-ordered) XOR `nextFromSlot` (slot-ordered). */
export interface QduelsTournamentSlotsPage {
  slots: QduelsTournamentSlot[];
  nextCursor: string | null;
  nextFromSlot: number | null;
}

export interface QduelsTournamentParticipantsPage {
  participants: QduelsTournamentParticipant[];
  nextCursor: string | null;
}

/**
 * One row of `GET /v1/tournaments/:id/standings`.
 *
 * ⚠️ `placement` is 1-based and **NOT densely consecutive** — tied players share
 * a placement and the next rung skips: on a size-8 bracket the two semifinal
 * losers are both `3`, and a round-1 loser is `5`, not `4`. Group by
 * `placement`; never assume row index + 1.
 */
export interface QduelsStanding {
  placement: number;
  logtoUserId: string;
  playerTag: string;
  /** CR in-game nick, or '' when unresolved. */
  playerName: string;
}

/**
 * `GET /v1/tournaments/:id/standings`. Only meaningful once the tournament is
 * `complete` (else 409 `tournament_not_complete`). `standings: []` when it
 * completed with no champion (a double-no-show final).
 */
export interface QduelsTournamentStandings {
  standings: QduelsStanding[];
}

// ─── Account linking (Clash Royale) ──────────────────────────────────────

/**
 * Request body for `POST /v1/players/verify-token`.
 *
 * Supercell verifies account ownership via a short-lived token the user
 * generates inside Clash Royale (Settings → API token for third-party
 * verification). The user pastes that token + their player tag; qduels checks
 * both against Supercell and, on success, links the caller's logtoUserId to
 * that CR tag.
 *
 * `playerTag` must include the leading `#` — the UI accepts it with or without
 * and normalizes before sending.
 */
export interface VerifyTokenRequest {
  playerTag: string;
  token: string;
}

/**
 * Response body for `POST /v1/players/verify-token`.
 * `verified: true` means the caller is now linked to `playerTag`. A wrong or
 * expired in-game token comes back as HTTP 422 with `{ verified: false }`.
 */
export interface VerifyTokenResponse {
  verified: boolean;
  playerTag: string;
}

/**
 * Response body for `GET /v1/players/me` (HTTP 200) — the caller's current CR
 * link. A 404 with `{ error: "no_linked_player" }` means the user has not
 * linked any Clash Royale account yet (treated as "not linked", not an error).
 */
export interface PlayerLinkDTO {
  logtoUserId: string;
  playerTag: string;
  verified: boolean;
  verifiedAt?: string | null;
}

/**
 * Minimal cached Clash Royale profile projection from
 * `GET /v1/players/:playerTag/profile`. Used only to show the linked name +
 * a couple of headline stats; optional, the link form works without it.
 */
export interface CrProfileDTO {
  name: string;
  expLevel?: number;
  trophies?: number;
  [key: string]: unknown;
}

/**
 * Response body for `GET /v1/players/by-cr-tag/:tag` (HTTP 200) — the identity
 * bridge for the CR side. A battle opponent / CR profile is known only by CR
 * tag (no email), so this maps a CR tag → Logto `sub` for players who linked
 * their account in qduels. 404 `{ error: "player_not_found" }` means the tag
 * isn't linked (treated as "not resolved", NOT an error); 400
 * `{ error: "invalid_player_tag" }` for a malformed tag.
 */
export interface PlayerByCrTagDTO {
  logtoUserId: string;
}

// ─── Open matchmaking queue (Clash Royale) ───────────────────────────────

/**
 * The match currently assigned to the caller while they're in the queue, as
 * projected inside `GET /v1/queue/status`. It carries ONLY the match's CR game
 * tag, lifecycle state, and game mode — NOT the opponent's tag (that would need
 * a separate `GET /v1/matches/:gameTag`). `currentMatch != null` is the "paired"
 * signal the QueueSheet flips to the "found" state on.
 */
export interface QueueCurrentMatch {
  gameTag: string;
  state: string;
  gameMode: string;
}

/**
 * Response body for `GET /v1/queue/status` (HTTP 200) — the caller's live
 * matchmaking state. Polled (~3s) only while the QueueSheet is open.
 * - `inQueue` / `isSearching`: whether the caller has an active queue entry.
 * - `position`: 1-based slot in the queue (0 when not queued).
 * - `searching`: total players currently searching the same pool — surfaced as
 *   "{n} in coda" and used to surface the friend CTA early when `<= 1`.
 * - `gameMode`: the pool the caller is searching (fixed `REGULAR` in v1).
 * - `currentMatch`: the assigned match once paired, else `null`.
 * - `lastRun`: ISO timestamp of the last pairing-worker run (diagnostic).
 */
export interface QueueStatus {
  inQueue: boolean;
  isSearching: boolean;
  position: number;
  searching: number;
  gameMode: string;
  currentMatch: QueueCurrentMatch | null;
  lastRun: string | null;
  /**
   * The check-in gate, when qduels' `FEATURE_MATCH_CHECKIN` is on: you have been
   * paired, and the in-game invite is held until BOTH confirm. `null` when the
   * gate is off or you are not in a pending pair — so no client flag is needed.
   *
   * Unlike the tournament slot's flat per-side fields, this block IS
   * caller-scoped: `checkedIn` means *you*, `opponentPlayerTag` means *yours* —
   * because it comes from a per-caller status read. Same feature, two honestly
   * different shapes.
   */
  checkin: {
    pairId: string;
    deadlineAt: string;
    checkedIn: boolean;
    bothCheckedIn: boolean;
    opponentPlayerTag: string | null;
  } | null;
}

/**
 * Request body for `POST /v1/queue/enter`. v1 always sends the single fixed
 * pool (`REGULAR`) and a single-game series (`bo1`) — no mode/format picker.
 */
export interface QueueEnterRequest {
  gameMode: DuelGameMode;
  format?: DuelFormat;
}

/**
 * Response body for `POST /v1/queue/enter` (HTTP 201) — the created (or, on a
 * re-enter, updated/idempotent) queue entry. The Mini App only needs to know
 * the request succeeded and then drives off `GET /v1/queue/status`, so we keep
 * the projection minimal and tolerant.
 */
export interface QueueEntry {
  inQueue: boolean;
  gameMode: string;
  position?: number;
  format?: string;
}

// ─── Match detail + post-match stats ─────────────────────────────────────

/**
 * A completed (or in-flight) match document from `GET /v1/matches/:gameTag`.
 * Mirrors qduels-api's `Match` model. Returned unwrapped (no `{ data }`).
 *
 * Identity is by CR tag: `playerTagA`/`playerTagB` are the two players,
 * `scoreA`/`scoreB` their crowns (REGULAR) or rounds (`duel` mode), and
 * `winner` is the winning player's CR tag (or `null` for a draw / no result).
 * The caller maps "me" by comparing their own CR tag (from `getMyLink`) to
 * `playerTagA`/`playerTagB`.
 */
export interface MatchDTO {
  gameTag: string;
  gameMode: string;
  state: string;
  playerTagA: string;
  playerTagB: string;
  /** Winning player's CR tag, or `null` for a draw / unresolved. */
  winner: string | null;
  /** Crowns (REGULAR) or rounds won (`duel`) for playerTagA / playerTagB. */
  scoreA: number;
  scoreB: number;
  pointsA: number;
  pointsB: number;
  /** `crowns` | `rounds` | `forfeit` | `timeout` | `random`, or null. */
  resultReason: string | null;
  replayTag?: string | null;
  challengeId?: string | null;
  queuePairId?: string | null;
  completedAt?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

/**
 * Per-player battle stats for one side of a match. Mirrors qduels-api's
 * `PlayerBattleStats` (domain/stats.ts). Each entry carries its CR `tag`, so
 * the caller attributes the "team"/"opponent" arrays to you vs the opponent by
 * tag rather than by array position.
 */
export interface PlayerBattleStats {
  tag: string;
  name: string;
  crowns: number;
  princessTowersHitPoints?: number[];
  kingTowerHitPoints?: number;
  elixirLeaked?: number;
  startingTrophies: number | null;
  globalRank: number | null;
  averageElixirCost: number;
  totalElixirCost: number;
  averageCardLevel: number;
  rarityDistribution: Record<string, number>;
  elixirDistribution: Record<string, number>;
  cycleCards: number;
  winConditions: number;
  spells: number;
  airTargetingCards: number;
  evolutionCapableCards: number;
}

/**
 * One player's deck as a Clash Royale "copy deck" deep link. Mirrors
 * qduels-api's `DeckDeeplink` (domain/deckLinks.ts). `cardIds` is a
 * semicolon-joined list of numeric card ids (`"26000000;..."`), `web` the
 * ready-to-open `link.clashroyale.com` URL, `native` the raw
 * `clashroyale://copyDeck…` scheme. Each entry carries its CR `tag`.
 */
export interface DeckDeeplink {
  tag: string;
  name: string;
  native: string;
  web: string;
  cardIds: string;
}

/**
 * Post-match stats bundle from `GET /v1/matches/:gameTag/stats`. Mirrors
 * qduels-api's `MatchStats` model. `stats`/`copyDecks` each split the two
 * players into `team` / `opponent` arrays — attribute by the `tag` each entry
 * carries, NOT by which array it's in.
 */
export interface MatchStatsDTO {
  gameTag: string;
  gameMode: string;
  replayTag: string | null;
  stats: {
    team: PlayerBattleStats[];
    opponent: PlayerBattleStats[];
  };
  copyDecks: {
    team: DeckDeeplink[];
    opponent: DeckDeeplink[];
  };
  viewCount: number;
  shareCount: number;
}

// ─── Ranked rating (GET /v1/rating) ──────────────────────────────────────────

/**
 * The caller's personal ranked standing. A STANDING, not a leaderboard: nothing
 * in qduels orders players by XP, so this can never become a ranking.
 *
 * `enabled: false` is the flag-off answer (`RATING_ENABLED`), and it comes back
 * 200 with nulls rather than an error — treat it as "no rating surface", not as
 * a failure. `xp` is null until the player's first queue entry: the rating
 * wallet is seeded on entering, never on reading.
 */
export interface QduelsRating {
  enabled: boolean;
  xp: number | null;
  tier: { name: string; index: number } | null;
  ratedCount: number;
}

/**
 * Tier thresholds, mirrored from qduels-api `src/domain/rating.ts:61-68`.
 *
 * Duplicated on purpose: the server sends the tier NAME and INDEX but never the
 * band's bounds, so a client that wants to draw "how far into Gold am I" has to
 * know where Gold starts and Platinum begins. Keep in sync by hand — same deal
 * as `src/types/qbot.ts`. If they drift, the ring fills wrong while the tier
 * label stays right, which is the tell.
 */
export const QDUELS_TIERS: ReadonlyArray<{ name: string; min: number }> = [
  { name: 'Bronze', min: 0 },
  { name: 'Silver', min: 600 },
  { name: 'Gold', min: 1000 },
  { name: 'Platinum', min: 1400 },
  { name: 'Diamond', min: 1800 },
  { name: 'Master', min: 2200 },
];

/** Rated matches needed to leave placement (server `RATING_PLACEMENT_MATCHES`). */
export const QDUELS_PLACEMENT_MATCHES = 10;

// ─── Tournament ladder (GET /v1/leagues, GET /v1/leaderboard) ────────────────

/**
 * One server-league the caller takes part in. `platform: 'global'` is the
 * server-less season (tournaments created without a guild); 'discord'/'telegram'
 * are per-server ladders. They coexist by design — a player can be in several.
 */
export interface QduelsUserLeague {
  platform: string;
  guildId: string;
  leagueId: string;
  /** 'YYYYMM'. */
  seasonMonth: string;
  scoringCurrencyId: string;
}

/** One row of a ladder. Carries only the Logto `sub` — names come from QSocials. */
export interface QduelsLadderRow {
  sub: string;
  rank: number;
  score: number;
}

/**
 * The caller's own standing, echoed by the server so a client can always say
 * "sei #N" even when the player is outside the fetched page. Typed since day one
 * and never rendered until now.
 */
export interface QduelsLadderCaller {
  sub: string;
  rank: number;
  score: number;
  tierName: string | null;
}

export interface QduelsLadder {
  season: { leagueId: string; month: string };
  rows: QduelsLadderRow[];
  pagination: { total: number; page: number; totalPages: number };
  caller: QduelsLadderCaller | null;
}
